import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getTimezoneDisplayWithOffset } from '@/utils/timezone-detection';

interface TimezoneDetectionAlertProps {
  userId: string;
  userType: 'client' | 'practitioner';
  currentTimezone?: string | null;
}

export const TimezoneDetectionAlert: React.FC<TimezoneDetectionAlertProps> = ({
  userId,
  userType,
  currentTimezone
}) => {
  const { t } = useTranslation('dashboard');
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (!currentTimezone) return;

    // Check if we've already shown the alert this session
    const sessionKey = `timezone-check-${userId}`;
    if (sessionStorage.getItem(sessionKey)) {
      return;
    }

    const detectAndCompareTimezone = async () => {
      if (hasChecked) return;
      
      try {
        // Get IP-based timezone from edge function
        const { data: locationData, error: locationError } = await supabase.functions.invoke(
          'get-approx-location'
        );

        if (locationError || !locationData?.timezone) {
          console.log('Could not detect timezone from IP');
          return;
        }

        const detectedTimezone = locationData.timezone;
        
        // Compare timezones - check if there's a significant difference (>= 1 hour)
        const currentOffset = getTimezoneOffset(currentTimezone);
        const detectedOffset = getTimezoneOffset(detectedTimezone);
        const hoursDifference = Math.abs(currentOffset - detectedOffset);

        if (hoursDifference >= 1) {
          // Show toast with update option
          showTimezoneUpdateToast(
            currentTimezone,
            detectedTimezone,
            userId,
            userType,
            sessionKey
          );
        } else {
          // Mark as checked even if no difference
          sessionStorage.setItem(sessionKey, 'checked');
        }
        
        setHasChecked(true);
      } catch (error) {
        console.error('Error detecting timezone:', error);
        // Don't show error to user, just silently fail
      }
    };

    // Run detection after a short delay to not block initial render
    const timer = setTimeout(detectAndCompareTimezone, 2000);
    return () => clearTimeout(timer);
  }, [userId, userType, currentTimezone, hasChecked]);

  return null; // This component doesn't render anything
};

// Helper function to get timezone offset in hours
const getTimezoneOffset = (timezone: string): number => {
  try {
    const now = new Date();
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    return (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
  } catch (error) {
    console.error('Error calculating timezone offset:', error);
    return 0;
  }
};

// Show timezone update toast
const showTimezoneUpdateToast = (
  currentTimezone: string,
  detectedTimezone: string,
  userId: string,
  userType: 'client' | 'practitioner',
  sessionKey: string
) => {
  const currentDisplay = getTimezoneDisplayWithOffset(currentTimezone);
  const detectedDisplay = getTimezoneDisplayWithOffset(detectedTimezone);

  toast({
    title: "Timezone Change Detected",
    description: (
      <div className="space-y-3 mt-2">
        <div className="text-sm space-y-1">
          <div>
            <span className="text-muted-foreground">Current setting: </span>
            <span className="font-medium">{currentDisplay}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Detected location: </span>
            <span className="font-medium">{detectedDisplay}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Your location appears to have changed. Would you like to update your timezone?
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() => handleUpdateTimezone(userId, userType, detectedTimezone, sessionKey)}
          >
            Update Timezone
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              sessionStorage.setItem(sessionKey, 'ignored');
              toast({ title: "Timezone update skipped" });
            }}
          >
            Keep Current
          </Button>
        </div>
      </div>
    ),
    duration: 15000, // Show for 15 seconds
  });
};

// Handle timezone update
const handleUpdateTimezone = async (
  userId: string,
  userType: 'client' | 'practitioner',
  newTimezone: string,
  sessionKey: string
) => {
  try {
    const table = userType === 'client' ? 'clients' : 'practitioners';
    
    const { error } = await supabase
      .from(table)
      .update({ timezone: newTimezone, updated_at: new Date().toISOString() })
      .eq('id', userId);

    if (error) throw error;

    // Mark as checked in session
    sessionStorage.setItem(sessionKey, 'updated');

    toast({
      title: "Timezone Updated",
      description: `Your timezone has been updated to ${getTimezoneDisplayWithOffset(newTimezone)}. Please refresh the page to see changes.`,
    });

    // Refresh page after 2 seconds to apply changes
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  } catch (error) {
    console.error('Error updating timezone:', error);
    toast({
      title: "Update Failed",
      description: "Could not update timezone. Please try again from settings.",
      variant: "destructive",
    });
  }
};
